import { useEffect, useRef, useState } from 'react';
import { useSocket } from '@/context/SocketContext';
import { Play, Square, BrainCircuit, Zap, Activity, Eye, BarChart2 } from 'lucide-react';

type FlyBrainVisualizerProps = {
  compact?: boolean;
};

export default function FlyBrainVisualizer({ compact = false }: FlyBrainVisualizerProps) {
  const { socket, flyBrainState } = useSocket();
  const [logLines, setLogLines] = useState<string[]>([]);
  const [active, setActive] = useState(false);
  const [targetMint, setTargetMint] = useState('7BGsqoZADzL9A1YvUovzS71j9k6W9d3Uu7o4aHjGpump');
  const logRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!socket) return;
    const onLog = (data: { msg: string }) =>
      setLogLines(prev => [`[${new Date().toISOString().split('T')[1].slice(0, 12)}] ${data.msg}`, ...prev].slice(0, 80));
    const onTrade = (data: { side: string; price?: number; pnl?: number }) =>
      setLogLines(prev => [`[TRADE] ${data.side.toUpperCase()} @ $${(data.price ?? 0).toFixed(6)}${data.pnl != null ? ` PnL: ${data.pnl > 0 ? '+' : ''}${data.pnl.toFixed(4)}` : ''}`, ...prev].slice(0, 80));

    socket.on('fly_brain_log', onLog);
    socket.on('fly_brain_trade', onTrade);
    return () => {
      socket.off('fly_brain_log', onLog);
      socket.off('fly_brain_trade', onTrade);
    };
  }, [socket]);

  const toggleEngine = () => {
    if (!socket) return;
    if (active) {
      socket.emit('fly_brain_stop');
      setActive(false);
    } else {
      socket.emit('fly_brain_start', { mint: targetMint });
      setActive(true);
    }
  };

  const dopamine = flyBrainState?.dopamine ?? 0;
  const dopNorm = Math.max(0, Math.min(1, (dopamine + 5) / 10));
  const dopColor = dopamine > 0.5 ? '#22c55e' : dopamine < -0.5 ? '#ef4444' : '#6b7280';

  // ─── COMPACT ────────────────────────────────────────────────────────────────
  if (compact) {
    return (
      <div className="flex flex-col bg-[#050508] border-b border-green-500/15 overflow-hidden">
        <div className="flex items-center gap-3 px-3 py-1.5 bg-gradient-to-r from-green-500/8 to-transparent border-b border-green-500/10">
          <BrainCircuit className="w-3.5 h-3.5 text-green-500/70 shrink-0" />
          <span className={`text-[9px] font-bold tracking-widest ${flyBrainState?.connected ? 'text-green-400' : 'text-zinc-600'}`}>
            {flyBrainState?.connected ? '● LIVE' : '○ IDLE'}
          </span>
          <div className="flex items-center gap-1" title={`Dopamine: ${flyBrainState?.dopamine?.toFixed(2) ?? '—'}`}>
            <Zap className="w-3 h-3" style={{ color: dopColor }} />
            <div className="w-14 h-1.5 rounded-full bg-zinc-800 overflow-hidden">
              <div className="h-full rounded-full transition-all duration-300" style={{ width: `${dopNorm * 100}%`, background: dopColor }} />
            </div>
          </div>
          <div className="flex items-center gap-1 text-purple-400/70" title="Spike rate">
            <Activity className="w-3 h-3" />
            <span className="text-[9px] font-mono tabular-nums">{flyBrainState?.spikeRate ?? 0}n</span>
          </div>
          <span className="text-[9px] text-green-700/80 font-mono tabular-nums" title="Tokens watched">👁 {flyBrainState?.watchedTokens ?? 0}</span>
          <span className="text-[9px] text-cyan-700/80 font-mono tabular-nums" title="Open positions">📊 {flyBrainState?.openPositions ?? 0} pos</span>
          {logLines[0] && (
            <span className="flex-1 truncate text-[9px] text-green-700/60 font-mono">{logLines[0]}</span>
          )}
        </div>
      </div>
    );
  }

  // ─── FULL (Sidebar) ─────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-full bg-[#050508] overflow-hidden">

      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-green-500/15 shrink-0 bg-black/40 backdrop-blur-md">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl bg-green-500/10 border border-green-500/20">
            <BrainCircuit className="w-5 h-5 text-green-500" />
          </div>
          <div>
            <h2 className="text-sm font-bold text-green-500 font-mono uppercase tracking-widest">Neural Connectome</h2>
            <span className={`flex items-center gap-1 text-[10px] uppercase font-bold tracking-widest ${active ? 'text-green-400' : 'text-zinc-600'}`}>
              {active ? '● Processing Market Data' : '○ Disconnected'}
            </span>
          </div>
        </div>

        <div className="flex gap-2">
          <input
            type="text"
            value={targetMint}
            onChange={e => setTargetMint(e.target.value)}
            disabled={active}
            className="w-40 bg-black/50 border border-green-500/20 rounded-lg px-3 py-1.5 text-xs text-green-300 focus:outline-none focus:border-green-500 font-mono placeholder-green-900"
            placeholder="Target mint…"
          />
          <button
            onClick={toggleEngine}
            className={`px-4 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-all flex items-center gap-2 ${
              active
                ? 'bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500/20'
                : 'bg-green-500/10 text-green-400 border border-green-500/30 hover:bg-green-500/20'
            }`}
          >
            {active ? <Square className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
            {active ? 'Halt' : 'Initialize'}
          </button>
        </div>
      </div>

      {/* Live stats strip */}
      <div className="grid grid-cols-2 gap-px bg-green-500/5 border-b border-green-500/10 shrink-0">

        {/* Dopamine */}
        <div className="flex flex-col gap-1.5 px-4 py-3 bg-[#050508]">
          <div className="flex items-center justify-between">
            <span className="text-[9px] uppercase tracking-widest font-bold text-green-500/50 flex items-center gap-1">
              <Zap className="w-3 h-3" /> Dopamine
            </span>
            <span className="text-[10px] font-mono font-bold" style={{ color: dopColor }}>
              {dopamine.toFixed(2)}
            </span>
          </div>
          <div className="w-full h-2 rounded-full bg-zinc-800/80 overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{ width: `${dopNorm * 100}%`, background: `linear-gradient(90deg, ${dopColor}88, ${dopColor})` }}
            />
          </div>
        </div>

        {/* Spike Rate */}
        <div className="flex flex-col gap-1 px-4 py-3 bg-[#050508]">
          <span className="text-[9px] uppercase tracking-widest font-bold text-purple-400/50 flex items-center gap-1">
            <Activity className="w-3 h-3" /> Spike Rate
          </span>
          <span className="text-lg font-black font-mono text-purple-400 leading-none">
            {flyBrainState?.spikeRate ?? 0}<span className="text-[10px] ml-0.5 font-normal opacity-60">n/s</span>
          </span>
        </div>

        {/* Watched tokens */}
        <div className="flex flex-col gap-1 px-4 py-3 bg-[#050508]">
          <span className="text-[9px] uppercase tracking-widest font-bold text-cyan-400/50 flex items-center gap-1">
            <Eye className="w-3 h-3" /> Watching
          </span>
          <span className="text-lg font-black font-mono text-cyan-400 leading-none">
            {flyBrainState?.watchedTokens ?? 0}<span className="text-[10px] ml-0.5 font-normal opacity-60"> tokens</span>
          </span>
        </div>

        {/* Open positions */}
        <div className="flex flex-col gap-1 px-4 py-3 bg-[#050508]">
          <span className="text-[9px] uppercase tracking-widest font-bold text-amber-400/50 flex items-center gap-1">
            <BarChart2 className="w-3 h-3" /> Positions
          </span>
          <span className="text-lg font-black font-mono text-amber-400 leading-none">
            {flyBrainState?.openPositions ?? 0}<span className="text-[10px] ml-0.5 font-normal opacity-60"> open</span>
          </span>
        </div>
      </div>

      {/* Layer legend */}
      <div className="flex items-center gap-4 px-4 py-2 border-b border-green-500/10 shrink-0">
        <div className="flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full bg-cyan-400 shadow-[0_0_6px_#22d3ee]" />
          <span className="text-[9px] text-cyan-400/70 uppercase tracking-widest font-bold">Sensory (0–19)</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full bg-purple-500 shadow-[0_0_6px_#a855f7]" />
          <span className="text-[9px] text-purple-400/70 uppercase tracking-widest font-bold">Hidden (20–79)</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full bg-red-500 shadow-[0_0_6px_#ef4444]" />
          <span className="text-[9px] text-red-400/70 uppercase tracking-widest font-bold">Motor (80–99)</span>
        </div>
      </div>

      {/* Synaptic log */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="px-3 py-1.5 bg-green-500/5 border-b border-green-500/10 flex items-center justify-between shrink-0">
          <span className="text-[10px] font-bold text-green-500/70 uppercase tracking-widest">Live Synaptic Log</span>
          {logLines.length > 0 && (
            <button
              onClick={() => setLogLines([])}
              className="text-[9px] text-white/20 hover:text-white/50 transition-colors"
            >
              Clear
            </button>
          )}
        </div>
        <div ref={logRef} className="flex-1 overflow-y-auto p-2 font-mono text-[10px] flex flex-col gap-0.5 custom-scrollbar">
          {logLines.length === 0 ? (
            <span className="text-white/20 italic">No signals yet…</span>
          ) : (
            logLines.map((line, i) => (
              <div
                key={i}
                className={`leading-relaxed ${line.startsWith('[TRADE]') ? 'text-amber-400' : 'text-green-400/70'}`}
              >
                {line}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
